import { Box, Typography } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";

import { LogoMarkIcon } from "~/feature/chart/components/ChartViewMode/LogoMarkIcon";
import { EMBED_GOLD } from "~/feature/embed/constants";

export const Root = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$border",
})<{ $border: boolean }>(({ $border, theme }) => ({
  boxSizing: "border-box",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  padding: theme.spacing(1.25),
  overflow: "hidden",
  backgroundColor: theme.palette.background.paper,
  border: $border
    ? `1px solid ${
        theme.palette.mode === "dark"
          ? theme.palette.primary.main
          : theme.palette.divider
      }`
    : "none",
  borderRadius: theme.shape.borderRadius,
}));

export const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  flexShrink: 0,
  minHeight: 38,
}));

export const Icon = styled("img")({
  width: 28,
  height: 28,
  borderRadius: "50%",
  objectFit: "cover",
  flexShrink: 0,
});

export const Title = styled(Typography)(({ theme }) => ({
  fontSize: 15,
  fontWeight: 700,
  lineHeight: 1.25,
  color: theme.palette.text.primary,
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
  overflow: "hidden",
}));

export const Odds = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$columns",
})<{ $columns: number }>(({ $columns, theme }) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${$columns}, minmax(0, 1fr))`,
  gap: theme.spacing(0.75),
}));

export const Row = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$consensus" && prop !== "$fullWidth",
})<{ $consensus: boolean; $fullWidth?: boolean }>(
  ({ $consensus, $fullWidth, theme }) => ({
    ...($fullWidth && { gridColumn: "1 / -1" }),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    height: 30,
    padding: theme.spacing(0, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: $consensus
      ? alpha(EMBED_GOLD, 0.16)
      : alpha(theme.palette.text.primary, 0.04),
  })
);

export const RowLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$consensus",
})<{ $consensus: boolean }>(({ $consensus, theme }) => ({
  flex: 1,
  minWidth: 0,
  fontSize: 13,
  fontWeight: $consensus ? 700 : 500,
  color: theme.palette.text.primary,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

export const RowValue = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  fontWeight: 700,
  flexShrink: 0,
  color: theme.palette.text.primary,
}));

export const BrandLogo = styled(LogoMarkIcon)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

// Pinned to the bottom, so the card keeps its shape when a block above it is
// switched off — or when a brand new market has no history to draw yet.
export const Footer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  flexShrink: 0,
  marginTop: "auto",
}));

export const Volume = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
}));

export const Branding = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1),
  paddingTop: theme.spacing(0.5),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

export const BrandingLeft = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  minWidth: 0,
}));

export const BrandingText = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.text.secondary,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

export const ViewEvent = styled("a")(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.text.secondary,
  textDecoration: "none",
  flexShrink: 0,

  "&:hover": {
    color: theme.palette.text.primary,
  },
}));
